import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';

const INPUT =
    'tw-block tw-h-11 tw-w-full tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-text-[15px] tw-text-gray-900 tw-placeholder-gray-400 tw-shadow-sm focus:tw-border-primary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary/20';

const LoginForm = ({
    onSuccess,
    onSignUpClick,
    onForgotPasswordClick,
    className = '',
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const { login } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            const success = await login(email, password);
            if (success) {
                onSuccess?.();
            } else {
                setError('Credentials do not match. Please try again.');
            }
        } catch (err) {
            console.error('Login error:', err);
            if (err?.non_field_errors) {
                setError(err.non_field_errors[0]);
            } else if (err?.detail) {
                setError(err.detail);
            } else if (typeof err === 'string') {
                setError(err);
            } else {
                setError('Login failed. Please check your email and password.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            onSubmit={handleSubmit}
            className={`tw-space-y-5 ${className}`}
        >
            <div className="tw-space-y-1.5">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                    Email
                </label>
                <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={INPUT}
                    placeholder="you@example.com"
                    required
                />
            </div>

            <div className="tw-space-y-1.5">
                <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                    Password
                </label>
                <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={INPUT}
                    placeholder="••••••••"
                    required
                />
            </div>

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none">
                    <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="tw-rounded tw-border-gray-300 tw-text-primary focus:tw-ring-primary/40"
                    />
                    <span className="tw-text-sm tw-text-gray-600">Remember me</span>
                </label>

                {onForgotPasswordClick && (
                    <button
                        type="button"
                        onClick={onForgotPasswordClick}
                        className="tw-text-sm tw-font-medium tw-text-primary hover:tw-text-primary-dark tw-transition"
                    >
                        Forgot password?
                    </button>
                )}
            </div>

            {error && (
                <div className="tw-rounded-lg tw-bg-red-50 tw-border tw-border-red-100 tw-px-3 tw-py-2.5 tw-text-sm tw-text-red-700">
                    {error}
                </div>
            )}

            <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                disabled={submitting}
                className="tw-w-full tw-flex tw-items-center tw-justify-center tw-h-11 tw-rounded-lg tw-font-semibold tw-text-white tw-bg-primary hover:tw-bg-primary-dark tw-transition-colors tw-shadow-md tw-disabled:tw-opacity-60 tw-disabled:tw-pointer-events-none"
            >
                {submitting ? (
                    <span className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-h-5 tw-w-5 tw-border-2 tw-border-white/30 tw-border-t-white tw-rounded-full tw-animate-spin" />
                        Signing in…
                    </span>
                ) : (
                    'Sign in'
                )}
            </motion.button>

            {onSignUpClick && (
                <p className="tw-text-center tw-text-sm tw-text-gray-600 tw-pt-1">
                    Don’t have an account?{' '}
                    <button
                        type="button"
                        onClick={onSignUpClick}
                        className="tw-font-semibold tw-text-primary hover:tw-text-primary-dark tw-transition"
                    >
                        Sign up
                    </button>
                </p>
            )}
        </motion.form>
    );
};

export default LoginForm;